import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, Archive } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function timeAgo(isoString) {
  if (!isoString) return "—";
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export default function ProjectDetailModal({ project, onClose, onArchived, token }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/projects/${project.id}/tasks`, { headers: authHeaders })
      .then(({ data }) => setTasks(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [project.id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleArchive = async () => {
    setArchiving(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/projects/${project.id}`, { status: "archived" }, { headers: authHeaders });
      onArchived(project.id);
    } catch { /* silent */ } finally { setArchiving(false); }
  };

  const inboxTasks = tasks.filter((t) => t.status === "inbox");
  const doneTasks  = tasks.filter((t) => t.status === "done");
  const progress   = tasks.length > 0 ? Math.round((doneTasks.length / tasks.length) * 100) : 0;
  const isWork     = project.category === "work";

  return (
    <div
      data-testid="project-detail-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(0,0,0,0.20)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "rgba(255,255,255,0.93)",
        backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
        border: "1px solid rgba(255,255,255,0.65)",
        borderRadius: "1.25rem",
        boxShadow: "0 20px 60px rgba(26,77,46,0.14)",
        width: "100%", maxWidth: "500px",
        maxHeight: "82vh", overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ padding: "1.5rem 1.75rem 1rem", borderBottom: "1px solid rgba(26,77,46,0.08)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <span style={{ fontSize: "2rem", lineHeight: 1 }}>{project.emoji}</span>
              <div>
                <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: "1.1rem", fontWeight: 700, color: "#1A4D2E", margin: 0 }}>
                  {project.title}
                </h2>
                <span style={{
                  display: "inline-block", marginTop: "0.25rem",
                  padding: "0.12rem 0.55rem", borderRadius: "2rem",
                  fontSize: "0.68rem", fontWeight: 700, fontFamily: "'Manrope', sans-serif",
                  textTransform: "uppercase", letterSpacing: "0.05em",
                  background: isWork ? "rgba(61,107,31,0.12)" : "rgba(212,165,116,0.15)",
                  color: isWork ? "#3d6b1f" : "#92400e",
                  border: isWork ? "1px solid rgba(61,107,31,0.25)" : "1px solid rgba(212,165,116,0.4)",
                }}>
                  {project.category}
                </span>
              </div>
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", padding: 0 }}>
              <X size={18} />
            </button>
          </div>

          {/* Stats */}
          <div style={{ display: "flex", gap: "1.5rem", marginTop: "0.875rem", flexWrap: "wrap" }}>
            <StatItem label="Created" value={timeAgo(project.created_at)} />
            <StatItem label="Last active" value={timeAgo(project.last_touched)} />
            {project.target_completion && (
              <StatItem label="Due" value={new Date(project.target_completion).toLocaleDateString()} />
            )}
            <StatItem label="Progress" value={`${progress}%`} />
          </div>

          {/* Progress bar */}
          <div style={{ marginTop: "0.75rem", height: "4px", background: "rgba(26,77,46,0.1)", borderRadius: "2px", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress}%`, background: "linear-gradient(90deg, #3d6b1f, #86c559)", borderRadius: "2px", transition: "width 0.5s ease" }} />
          </div>

          {project.notes && (
            <p style={{ fontFamily: "'Manrope', sans-serif", color: "#64748b", fontSize: "0.8rem", marginTop: "0.625rem", fontStyle: "italic", margin: "0.625rem 0 0" }}>
              {project.notes}
            </p>
          )}
        </div>

        {/* Tasks */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1rem 1.75rem" }}>
          {loading ? (
            <p style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.875rem", textAlign: "center", padding: "1rem 0" }}>
              Loading tasks...
            </p>
          ) : tasks.length === 0 ? (
            <p style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.875rem", textAlign: "center", padding: "1rem 0" }}>
              No tasks assigned to this project yet.
            </p>
          ) : (
            <>
              {inboxTasks.length > 0 && <TaskGroup label={`Active (${inboxTasks.length})`} tasks={inboxTasks} done={false} />}
              {doneTasks.length > 0 && <TaskGroup label={`Completed (${doneTasks.length})`} tasks={doneTasks} done={true} />}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "1rem 1.75rem", borderTop: "1px solid rgba(26,77,46,0.08)", display: "flex", justifyContent: "flex-end" }}>
          <button
            data-testid="archive-project-btn"
            onClick={handleArchive}
            disabled={archiving}
            style={{
              display: "flex", alignItems: "center", gap: "0.4rem",
              padding: "0.5rem 1.25rem", borderRadius: "2rem",
              border: "1.5px solid rgba(251,191,36,0.45)",
              background: "rgba(251,191,36,0.08)",
              color: "#92400e",
              fontFamily: "'Manrope', sans-serif", fontSize: "0.875rem", fontWeight: 600,
              cursor: archiving ? "not-allowed" : "pointer",
              opacity: archiving ? 0.6 : 1,
            }}
          >
            <Archive size={14} />
            {archiving ? "Archiving..." : "Archive Project"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatItem({ label, value }) {
  return (
    <div>
      <p style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", margin: 0 }}>{label}</p>
      <p style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.8rem", fontWeight: 600, margin: 0 }}>{value}</p>
    </div>
  );
}

function TaskGroup({ label, tasks, done }) {
  return (
    <div style={{ marginBottom: "1rem" }}>
      <p style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 0.4rem" }}>
        {label}
      </p>
      {tasks.map((t) => (
        <div key={t.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0", borderBottom: "1px solid rgba(26,77,46,0.06)" }}>
          <div style={{
            width: "14px", height: "14px", borderRadius: "50%", flexShrink: 0,
            border: `1.5px solid ${done ? "#3d6b1f" : "rgba(26,77,46,0.3)"}`,
            background: done ? "#3d6b1f" : "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {done && (
              <svg width="8" height="6" viewBox="0 0 9 7" fill="none">
                <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
          <p style={{ fontFamily: "'Manrope', sans-serif", color: done ? "#9ca3af" : "#1A4D2E", fontSize: "0.85rem", textDecoration: done ? "line-through" : "none", margin: 0 }}>
            {t.title}
          </p>
        </div>
      ))}
    </div>
  );
}
